import "./bulk-tender-insurance-modal.css";

import { useQueryClient } from "@tanstack/react-query";
import { Loader } from "@tql/ui-components-react/kendo/indicators";
import { MessageBox } from "@tql/ui-components-react/tql/layout/beta";
import { useEffect, useMemo, useRef, useState } from "react";

import {
	useRemoveInsuranceMutation,
	useSaveInsuranceMutation,
} from "~/features/quote/components/carrier-selection/components/add-insurance-modal/add-insurance-hooks.tsx";
import { useSaveSelectedCarrier } from "~/features/quote/hooks/use-carrier-selection-hooks.ts";
import { useGetQuote } from "~/features/quote/hooks/use-quote-details-hooks.ts";
import type { InsuranceFormData } from "~/shared/components/insurance-form/insurance-form.tsx";
import { InsuranceForm } from "~/shared/components/insurance-form/insurance-form.tsx";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import type {
	CustomerQuoteBoardItem,
	QuoteBoardItem,
} from "~/shared/schemas/quote-board-schema.ts";
import type { QuoteResponse } from "~/shared/schemas/quote-schema.ts";

type BulkTenderInsuranceModalProps = {
	quoteId: number;
	initialQuote?: BulkTenderInsuranceBoardQuote;
	onClose: () => void;
	onComplete: (insuranceApplied?: boolean) => void;
	onInsuranceStateChange?: (quoteId: number, insuranceApplied: boolean) => void;
};

export type BulkTenderInsuranceBoardQuote = Pick<
	QuoteBoardItem | CustomerQuoteBoardItem,
	| "eTag"
	| "insuranceApplyTypeId"
	| "insuranceProductTypeId"
	| "insuranceValue"
	| "maxLiabilityNew"
	| "maxLiabilityOverride"
	| "maxLiabilityUsed"
	| "quoteId"
	| "selectedCarrierPriceId"
>;

type InsuranceQuote = {
	eTag: string;
	id: number;
	insuranceApplyTypeId: number | null;
	insuranceProductTypeId: number | null;
	insuranceValue: number | null;
	selectedCarrierPriceId: number | null;
};

type InsuranceMutationOutcome =
	| { success: true; error?: string }
	| { success: false; error?: string };

type RestoredQuoteOutcome =
	| { success: true; quote: QuoteResponse }
	| { success: false; error?: string };

type InsuranceRecoveryOutcome = "restored" | "refreshed" | "unconfirmed";

const hasInsurance = (quote: {
	insuranceApplyTypeId?: number | null;
}): boolean => quote.insuranceApplyTypeId != null;

const normalizeInsuranceQuote = (
	quote: QuoteResponse | BulkTenderInsuranceBoardQuote,
): InsuranceQuote => {
	if ("id" in quote) {
		return {
			eTag: quote.eTag,
			id: quote.id,
			insuranceApplyTypeId: quote.insuranceApplyTypeId,
			insuranceProductTypeId: quote.insuranceProductTypeId,
			insuranceValue: quote.insuranceValue,
			selectedCarrierPriceId: quote.selectedCarrierPriceId,
		};
	}

	return {
		eTag: quote.eTag ?? "",
		id: quote.quoteId,
		insuranceApplyTypeId: quote.insuranceApplyTypeId ?? null,
		insuranceProductTypeId: quote.insuranceProductTypeId ?? null,
		insuranceValue: quote.insuranceValue ?? null,
		selectedCarrierPriceId: quote.selectedCarrierPriceId ?? null,
	};
};

const getQuoteUnavailableMessage = (quoteLoadError: unknown): string => {
	if (quoteLoadError instanceof Error) return quoteLoadError.message;
	return "The quote could not be loaded. Close this window and try again.";
};

export const BulkTenderInsuranceModal = ({
	quoteId,
	initialQuote,
	onClose,
	onComplete,
	onInsuranceStateChange,
}: BulkTenderInsuranceModalProps) => {
	const queryClient = useQueryClient();
	const { isInternal } = useDeploymentType();
	const boardQuote = useMemo(
		() => (initialQuote ? normalizeInsuranceQuote(initialQuote) : null),
		[initialQuote],
	);
	const shouldFetchQuote = boardQuote == null || boardQuote.eTag.length === 0;
	const {
		data: fetchedQuote,
		isError: isQuoteLoadError,
		isLoading: isQuoteLoading,
		refetch: refetchQuote,
		error: quoteLoadError,
	} = useGetQuote(quoteId, { enabled: shouldFetchQuote });
	const saveInsuranceMutation = useSaveInsuranceMutation(isInternal);
	const removeInsuranceMutation = useRemoveInsuranceMutation(isInternal);
	const { mutateAsync: saveSelectedCarrier } = useSaveSelectedCarrier();
	const [refreshedQuote, setRefreshedQuote] = useState<InsuranceQuote | null>(
		null,
	);
	let quote = refreshedQuote;
	quote ??= shouldFetchQuote ? (fetchedQuote ?? null) : boardQuote;
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [activeAction, setActiveAction] = useState<
		"saving" | "removing" | null
	>(null);
	const isPending =
		saveInsuranceMutation.isPending ||
		removeInsuranceMutation.isPending ||
		activeAction !== null;
	const hasReportedInsuranceState = useRef(false);

	useEffect(() => {
		if (quote && !hasReportedInsuranceState.current) {
			hasReportedInsuranceState.current = true;
			onInsuranceStateChange?.(quoteId, hasInsurance(quote));
		}
	}, [onInsuranceStateChange, quote, quoteId]);

	const synchronizeQuoteState = (updatedQuote: QuoteResponse): void => {
		const normalizedQuote = normalizeInsuranceQuote(updatedQuote);
		queryClient.setQueryData(["getQuote", quoteId], updatedQuote);
		setRefreshedQuote(normalizedQuote);
		onInsuranceStateChange?.(quoteId, hasInsurance(normalizedQuote));
	};

	const refreshQuoteState = async (): Promise<boolean> => {
		const refreshed = await refetchQuote();
		if (!refreshed.data) return false;
		synchronizeQuoteState(refreshed.data);
		return true;
	};

	const refreshAfterConflict = async () => {
		await refreshQuoteState();
		setErrorMessage(
			"This quote was updated. The insurance form has been refreshed; review and save again.",
		);
	};

	const getMutationError = async (result: {
		error: string;
		status?: number;
	}): Promise<InsuranceMutationOutcome> => {
		if (result.status === 412) {
			await refreshAfterConflict();
			return { success: false };
		}
		return { success: false, error: result.error };
	};

	if (shouldFetchQuote && isQuoteLoading) {
		return (
			<TraxModal
				cancelText={"Cancel"}
				onCancel={onClose}
				onClose={onClose}
				showConfirmationButtons={true}
				title={"Add Insurance"}
				width={355}
			>
				<div
					aria-label={"Loading quote insurance details"}
					className={"bulk-tender-insurance-loading"}
					role={"status"}
				>
					<Loader size={"medium"} type={"infinite-spinner"} />
				</div>
			</TraxModal>
		);
	}

	if (!quote || (shouldFetchQuote && isQuoteLoadError)) {
		return (
			<TraxModal
				cancelText={"Close"}
				onCancel={onClose}
				onClose={onClose}
				showConfirmButton={false}
				showConfirmationButtons={true}
				title={"Add Insurance"}
				type={"error"}
				width={355}
			>
				<MessageBox status={"error"} title={"Insurance Unavailable"}>
					{getQuoteUnavailableMessage(quoteLoadError)}
				</MessageBox>
			</TraxModal>
		);
	}

	const insuranceIsApplied = hasInsurance(quote);

	const restoreSelectedCarrier = async (
		updatedQuote: QuoteResponse,
		insuranceApplyTypeId: number | null,
		insuranceProductTypeId: number | null,
		insuranceValue: number | null,
	): Promise<RestoredQuoteOutcome> => {
		if (
			quote.selectedCarrierPriceId == null ||
			updatedQuote.selectedCarrierPriceId === quote.selectedCarrierPriceId
		) {
			return { success: true, quote: updatedQuote };
		}

		const selectionResult = await saveSelectedCarrier({
			id: quote.id,
			eTag: updatedQuote.eTag,
			selectedCarrierPriceId: quote.selectedCarrierPriceId,
			insuranceApplyTypeId,
			insuranceProductTypeId,
			insuranceValue,
		});
		if (!selectionResult.success) {
			const mutationError = await getMutationError(selectionResult);
			return mutationError.success
				? { success: true, quote: updatedQuote }
				: mutationError;
		}

		return { success: true, quote: selectionResult.data };
	};

	const refreshInsuranceRecoveryState =
		async (): Promise<InsuranceRecoveryOutcome> => {
			try {
				return (await refreshQuoteState()) ? "refreshed" : "unconfirmed";
			} catch {
				return "unconfirmed";
			}
		};

	const restoreOriginalInsurance = async (
		eTag: string,
	): Promise<InsuranceRecoveryOutcome> => {
		try {
			const restoreResult = await saveInsuranceMutation.mutateAsync({
				eTag,
				id: quote.id,
				insuranceApplyTypeId: quote.insuranceApplyTypeId,
				insuranceProductTypeId: quote.insuranceProductTypeId,
				insuranceValue: quote.insuranceValue,
				selectedCarrierPriceId: quote.selectedCarrierPriceId,
			});
			if (!restoreResult.success) {
				return await refreshInsuranceRecoveryState();
			}
			synchronizeQuoteState(restoreResult.data);

			const restoredQuote = await restoreSelectedCarrier(
				restoreResult.data,
				quote.insuranceApplyTypeId,
				quote.insuranceProductTypeId,
				quote.insuranceValue,
			);
			if (!restoredQuote.success) {
				return await refreshInsuranceRecoveryState();
			}

			synchronizeQuoteState(restoredQuote.quote);
			return "restored";
		} catch {
			return await refreshInsuranceRecoveryState();
		}
	};

	const recoverFailedInsuranceReplacement = async (
		removedQuote: QuoteResponse,
		error: string,
	): Promise<InsuranceMutationOutcome> => {
		synchronizeQuoteState(removedQuote);
		const recoveryOutcome = await restoreOriginalInsurance(removedQuote.eTag);
		if (recoveryOutcome === "restored") {
			return { success: false, error };
		}
		if (recoveryOutcome === "refreshed") {
			return {
				success: false,
				error:
					"Insurance replacement could not be confirmed. The latest quote was loaded; review its insurance before trying again.",
			};
		}
		return {
			success: false,
			error:
				"Insurance replacement could not be confirmed. Close this window and reopen the quote before trying again.",
		};
	};

	const saveRealInsurance = async (
		formData: InsuranceFormData,
	): Promise<InsuranceMutationOutcome> => {
		let eTag = quote.eTag;
		let replacementBaseQuote: QuoteResponse | null = null;
		if (insuranceIsApplied) {
			const removeResult = await removeInsuranceMutation.mutateAsync({
				eTag,
				id: quote.id,
			});
			if (!removeResult.success) {
				return await getMutationError(removeResult);
			}
			eTag = removeResult.data.eTag;
			replacementBaseQuote = removeResult.data;
		}

		let result: Awaited<ReturnType<typeof saveInsuranceMutation.mutateAsync>>;
		try {
			result = await saveInsuranceMutation.mutateAsync({
				eTag,
				id: quote.id,
				insuranceApplyTypeId: formData.applyTo,
				insuranceProductTypeId: formData.product,
				insuranceValue: formData.shipmentValue,
				selectedCarrierPriceId: quote.selectedCarrierPriceId,
			});
		} catch (error) {
			if (replacementBaseQuote) {
				return await recoverFailedInsuranceReplacement(
					replacementBaseQuote,
					error instanceof Error
						? error.message
						: "Insurance could not be saved. Please try again.",
				);
			}
			throw error;
		}
		if (!result.success) {
			return replacementBaseQuote
				? await recoverFailedInsuranceReplacement(
						replacementBaseQuote,
						result.error,
					)
				: await getMutationError(result);
		}

		const restoredQuote = await restoreSelectedCarrier(
			result.data,
			formData.applyTo,
			formData.product,
			formData.shipmentValue,
		);
		if (!restoredQuote.success) return restoredQuote;

		queryClient.setQueryData(["getQuote", quote.id], restoredQuote.quote);
		return { success: true };
	};

	const handleSubmit = async (formData: InsuranceFormData) => {
		setErrorMessage(null);
		if (isPending) return;
		setActiveAction("saving");

		try {
			const result = await saveRealInsurance(formData);
			if (!result.success) {
				if (result.error) setErrorMessage(result.error);
				return;
			}
			onComplete(true);
		} catch (error) {
			setErrorMessage(
				error instanceof Error
					? error.message
					: "Insurance could not be saved. Please try again.",
			);
		} finally {
			setActiveAction(null);
		}
	};

	const handleRemove = async () => {
		setErrorMessage(null);
		if (isPending) return;
		setActiveAction("removing");

		try {
			const result = await removeInsuranceMutation.mutateAsync({
				eTag: quote.eTag,
				id: quote.id,
			});
			if (!result.success) {
				const mutationError = await getMutationError(result);
				const mutationErrorMessage = mutationError.success
					? undefined
					: mutationError.error;
				if (mutationErrorMessage) setErrorMessage(mutationErrorMessage);
				return;
			}

			const restoredQuote = await restoreSelectedCarrier(
				result.data,
				null,
				result.data.insuranceProductTypeId,
				result.data.insuranceValue,
			);
			if (!restoredQuote.success) {
				if (restoredQuote.error) setErrorMessage(restoredQuote.error);
				return;
			}

			queryClient.setQueryData(["getQuote", quote.id], {
				...restoredQuote.quote,
				insuranceApplyTypeId: null,
			});
			onComplete(false);
		} catch (error) {
			setErrorMessage(
				error instanceof Error
					? error.message
					: "Insurance could not be removed. Please try again.",
			);
		} finally {
			setActiveAction(null);
		}
	};

	return (
		<InsuranceForm
			key={quote.eTag}
			className={"bulk-tender-insurance-form"}
			errorMessage={errorMessage ?? undefined}
			hideApplyTo={true}
			initialValues={{
				product: quote.insuranceProductTypeId ?? undefined,
				shipmentValue: quote.insuranceValue,
			}}
			isSubmitting={isPending}
			onClose={onClose}
			onRemove={insuranceIsApplied ? () => void handleRemove() : undefined}
			onSubmit={(formData) => {
				void handleSubmit(formData);
			}}
			submittingLabel={
				activeAction === "removing" ? "Removing insurance" : "Saving insurance"
			}
			title={"Add Insurance"}
			width={"355px"}
		/>
	);
};
